export interface WeightAxis {
	min: number;
	max: number;
}

const DEFAULT_AXIS: WeightAxis = { min: 100, max: 900 };

const SFNT_SIGNATURES = new Set([0x00010000, 0x74727565, 0x4f54544f]);
const WOFF = 0x774f4646;
const WOFF2 = 0x774f4632;
const WOFF2_FVAR_INDEX = 47;
const WOFF2_CUSTOM_TAG = 63;
const WOFF2_GLYF_INDEX = 10;
const WOFF2_LOCA_INDEX = 11;

const tagAt = (view: DataView, offset: number) =>
	String.fromCharCode(view.getUint8(offset), view.getUint8(offset + 1), view.getUint8(offset + 2), view.getUint8(offset + 3));

const fixedAt = (view: DataView, offset: number) => view.getInt32(offset) / 65536;

const weightAxisFromFvar = (view: DataView): WeightAxis | null => {
	const axesOffset = view.getUint16(4);
	const axisCount = view.getUint16(8);
	const axisSize = view.getUint16(10);
	for (let index = 0; index < axisCount; index++) {
		const at = axesOffset + index * axisSize;
		if (at + 16 > view.byteLength) break;
		if (tagAt(view, at) === "wght") return { min: Math.round(fixedAt(view, at + 4)), max: Math.round(fixedAt(view, at + 12)) };
	}
	return null;
};

const sfntAxis = (view: DataView): WeightAxis | false => {
	const tables = view.getUint16(4);
	for (let index = 0; index < tables; index++) {
		const record = 12 + index * 16;
		if (tagAt(view, record) !== "fvar") continue;
		const offset = view.getUint32(record + 8);
		const length = view.getUint32(record + 12);
		return weightAxisFromFvar(new DataView(view.buffer, view.byteOffset + offset, length)) ?? false;
	}
	return false;
};

const inflate = async (bytes: Uint8Array) => {
	if (typeof DecompressionStream === "undefined") return null;
	const stream = new Blob([bytes.slice()]).stream().pipeThrough(new DecompressionStream("deflate"));
	return new Uint8Array(await new Response(stream).arrayBuffer());
};

const woffAxis = async (view: DataView): Promise<WeightAxis | false> => {
	const tables = view.getUint16(12);
	for (let index = 0; index < tables; index++) {
		const entry = 44 + index * 20;
		if (tagAt(view, entry) !== "fvar") continue;
		const offset = view.getUint32(entry + 4);
		const compressed = view.getUint32(entry + 8);
		const original = view.getUint32(entry + 12);
		const raw = new Uint8Array(view.buffer, view.byteOffset + offset, compressed);
		const bytes = compressed < original ? await inflate(raw).catch(() => null) : raw;
		if (!bytes) return DEFAULT_AXIS;
		return weightAxisFromFvar(new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength)) ?? false;
	}
	return false;
};

const readBase128 = (view: DataView, offset: number) => {
	let value = 0;
	for (let index = 0; index < 5; index++) {
		const byte = view.getUint8(offset + index);
		value = (value << 7) | (byte & 0x7f);
		if (!(byte & 0x80)) return { value, next: offset + index + 1 };
	}
	throw new Error("bad UIntBase128");
};

const woff2HasFvar = (view: DataView) => {
	const tables = view.getUint16(12);
	let offset = 48;
	for (let index = 0; index < tables; index++) {
		const flags = view.getUint8(offset++);
		const tagIndex = flags & 0x3f;
		let tag = "";
		if (tagIndex === WOFF2_CUSTOM_TAG) {
			tag = tagAt(view, offset);
			offset += 4;
		}
		if (tagIndex === WOFF2_FVAR_INDEX || tag === "fvar") return true;
		offset = readBase128(view, offset).next;
		const transform = (flags >> 6) & 3;
		const isGlyphTable = tagIndex === WOFF2_GLYF_INDEX || tagIndex === WOFF2_LOCA_INDEX;
		if (isGlyphTable ? transform === 0 : transform !== 0) offset = readBase128(view, offset).next;
	}
	return false;
};

const axisOfBytes = async (buffer: ArrayBuffer): Promise<WeightAxis | false | null> => {
	const view = new DataView(buffer);
	if (view.byteLength < 12) return null;
	const signature = view.getUint32(0);
	if (SFNT_SIGNATURES.has(signature)) return sfntAxis(view);
	if (signature === WOFF) return woffAxis(view);
	if (signature === WOFF2) return woff2HasFvar(view) ? DEFAULT_AXIS : false;
	return null;
};

export const readWeightAxis = async (source: string): Promise<WeightAxis | false | null> => {
	try {
		const response = await fetch(source);
		if (!response.ok) return null;
		return await axisOfBytes(await response.arrayBuffer());
	} catch {
		return null;
	}
};

export const weightRange = ({ min, max }: WeightAxis) => (min < max ? `${min} ${max}` : String(min));
